import React, { useState, useEffect } from "react";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";
import { useCurrencies } from "@/hooks/useCurrencies";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";

export function DefaultCurrencyStep() {
  const { t } = useEnhancedTranslation();
  const { user } = useAuth();
  const { useCurrenciesQuery } = useCurrencies();
  const { data: currencies, isLoading } = useCurrenciesQuery();
  const queryClient = useQueryClient();
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load current default
  useEffect(() => {
    if (!user) return;
    const loadDefault = async () => {
      const { data } = await supabase
        .from("user_preferences")
        .select("default_currency_id")
        .eq("user_id", user.id)
        .single();
      if (data?.default_currency_id) {
        setSelectedCurrencyId(data.default_currency_id);
      }
    };
    loadDefault();
  }, [user]);

  const handleSelect = async (currencyId: string) => {
    if (!user) return;
    setSelectedCurrencyId(currencyId);
    setSaving(true);
    setSaved(false);
    
    const { error } = await supabase
      .from("user_preferences")
      .update({ default_currency_id: currencyId })
      .eq("user_id", user.id);

    if (error) {
      toast.error(t("setupGuide.defaultCurrency.error", "Failed to set default currency"));
    } else {
      // Invalidate userPreferences cache so settings page reflects the change
      queryClient.invalidateQueries({ queryKey: ['userPreferences'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
    setSaving(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-center">
        {t("setupGuide.defaultCurrency.title", "Choose Your Default Currency")}
      </h2>
      
      <p className="text-center text-muted-foreground text-sm">
        {t("setupGuide.defaultCurrency.description", "This will be used as the default when creating new contracts. You can change it anytime in Settings.")}
      </p>

      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        {currencies?.map((currency) => (
          <Button
            key={currency.id}
            variant={selectedCurrencyId === currency.id ? "default" : "outline"}
            className="h-auto py-3 px-4 flex flex-col items-center gap-1 relative"
            onClick={() => handleSelect(currency.id)}
            disabled={saving}
          >
            <span className="text-lg font-bold">{currency.symbol}</span>
            <span className="text-xs">{currency.code} – {currency.name}</span>
            {selectedCurrencyId === currency.id && (
              <Check className="h-4 w-4 absolute top-2 right-2" />
            )}
          </Button>
        ))}
      </div>

      {saved && (
        <p className="text-center text-sm text-primary">
          {t("setupGuide.defaultCurrency.saved", "Default currency updated!")}
        </p>
      )}
    </div>
  );
}
